import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { 
  Employee, 
  Department, 
  defaultEmployees, 
  defaultDepartments, 
  defaultStats,
  defaultTrendData,
  generateEmployeeDataset,
  calculateDepartmentStats,
  calculateOverallStats,
  generateTrendData
} from '../data/employees';

interface Stats {
  totalEmployees: number;
  highRiskCount: number;
  mediumRiskCount: number;
  lowRiskCount: number;
  percentDisengaged: number;
  avgRiskScore: number;
  estimatedCost: number;
  trendingDown: number;
  trendingStable: number;
  trendingUp: number;
  topBehavioralFlags: { flag: string; count: number }[];
  avgSalary: number;
}

interface TrendDataPoint {
  week: string;
  fullDate: string;
  highRisk: number;
  mediumRisk: number;
  lowRisk: number;
  avgScore: number;
}

interface AppContextType {
  employees: Employee[];
  departments: Department[];
  stats: Stats;
  trendData: TrendDataPoint[];
  isLoading: boolean;
  selectedEmployee: Employee | null;
  setSelectedEmployee: (employee: Employee | null) => void;
  refreshData: () => void;
  uploadCSV: (file: File) => Promise<void>;
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  user: { name: string; email: string } | null;
  setUser: (user: { name: string; email: string } | null) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  // Load authentication state from localStorage on mount
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const stored = localStorage.getItem('dynami_auth');
    return stored === 'true';
  });
  
  const [user, setUser] = useState<{ name: string; email: string } | null>(() => {
    const stored = localStorage.getItem('dynami_user');
    return stored ? JSON.parse(stored) : null;
  });

  const [employees, setEmployees] = useState<Employee[]>(defaultEmployees);
  const [departments, setDepartments] = useState<Department[]>(defaultDepartments);
  const [stats, setStats] = useState<Stats>(defaultStats);
  const [trendData, setTrendData] = useState<TrendDataPoint[]>(defaultTrendData);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  // Sync authentication state to localStorage
  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem('dynami_auth', 'true');
    } else {
      localStorage.removeItem('dynami_auth');
      localStorage.removeItem('dynami_user');
    }
  }, [isAuthenticated]);

  // Sync user to localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem('dynami_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('dynami_user');
    }
  }, [user]);

  const refreshData = useCallback(() => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      const newEmployees = generateEmployeeDataset(80);
      setEmployees(newEmployees);
      setDepartments(calculateDepartmentStats(newEmployees));
      setStats(calculateOverallStats(newEmployees));
      setTrendData(generateTrendData(12));
      setIsLoading(false);
    }, 1500);
  }, []);

  const uploadCSV = useCallback(async (file: File): Promise<void> => {
    setIsLoading(true);
    
    // Simulate CSV parsing and processing
    return new Promise((resolve) => {
      setTimeout(() => {
        // Generate new "analyzed" data based on "upload"
        const employeeCount = Math.floor(Math.random() * 40) + 60; // 60-100 employees
        const newEmployees = generateEmployeeDataset(employeeCount);
        setEmployees(newEmployees);
        setDepartments(calculateDepartmentStats(newEmployees));
        setStats(calculateOverallStats(newEmployees));
        setTrendData(generateTrendData(12));
        setIsLoading(false);
        resolve();
      }, 2500); // Longer delay to simulate "processing"
    });
  }, []);

  return (
    <AppContext.Provider
      value={{
        employees,
        departments,
        stats,
        trendData,
        isLoading,
        selectedEmployee,
        setSelectedEmployee,
        refreshData,
        uploadCSV,
        isAuthenticated,
        setIsAuthenticated,
        user,
        setUser,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}


